import type { AccordionToggleEventHandler } from '@fluentui/react-components'
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Divider,
} from '@fluentui/react-components'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PROMPT_CATEGORIES, type Prompt, type PromptCategory } from '../../types/prompt'
import { PromptCard } from '../PromptCard/PromptCard'
import { useStyles } from './StandardPromptAccordion.styles'

const UNCATEGORIZED_CATEGORY_KEY = 'uncategorized' as const
type AccordionCategoryKey = PromptCategory | typeof UNCATEGORIZED_CATEGORY_KEY

const isKnownCategory = (category: string): category is PromptCategory => {
  return PROMPT_CATEGORIES.includes(category as PromptCategory)
}

interface StandardPromptAccordionProps {
  prompts: Prompt[]
}

const resolveCategoryKey = (category: Prompt['category']) => {
  if (!category) {
    return UNCATEGORIZED_CATEGORY_KEY
  }

  if (isKnownCategory(category)) {
    return category
  }

  return UNCATEGORIZED_CATEGORY_KEY
}

const getCategoryLabel = (
  categoryKey: AccordionCategoryKey,
  translate: (key: string) => string
) => {
  if (categoryKey === UNCATEGORIZED_CATEGORY_KEY) {
    return translate('prompts.categories.uncategorized')
  }

  return translate(`prompts.categories.${categoryKey}`)
}

const StandardPromptAccordion = ({ prompts }: StandardPromptAccordionProps) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const groupedPrompts = useMemo(() => {
    const grouped = new Map<AccordionCategoryKey, Prompt[]>()

    prompts.forEach((prompt) => {
      const categoryKey = resolveCategoryKey(prompt.category)

      if (categoryKey === UNCATEGORIZED_CATEGORY_KEY) {
        console.error('[StandardPromptAccordion] Prompt has missing/unknown category', {
          promptId: prompt.promptId,
          title: prompt.title,
          category: prompt.category,
        })
      }

      const existingPrompts = grouped.get(categoryKey) ?? []
      grouped.set(categoryKey, [...existingPrompts, prompt])
    })

    return grouped
  }, [prompts])

  const visibleCategoryKeys = useMemo<AccordionCategoryKey[]>(() => {
    const orderedCategoryKeys: AccordionCategoryKey[] = PROMPT_CATEGORIES.filter((categoryKey) =>
      groupedPrompts.has(categoryKey)
    )

    if (groupedPrompts.has(UNCATEGORIZED_CATEGORY_KEY)) {
      orderedCategoryKeys.push(UNCATEGORIZED_CATEGORY_KEY)
    }

    return orderedCategoryKeys
  }, [groupedPrompts])

  const [openItem, setOpenItem] = useState<AccordionCategoryKey | undefined>(undefined)

  const effectiveOpenItem =
    openItem && visibleCategoryKeys.includes(openItem) ? openItem : undefined

  const handleToggle: AccordionToggleEventHandler<string> = (_, data) => {
    const [nextOpenItem] = data.openItems

    setOpenItem(
      nextOpenItem && groupedPrompts.has(nextOpenItem as AccordionCategoryKey)
        ? (nextOpenItem as AccordionCategoryKey)
        : undefined
    )
  }

  if (visibleCategoryKeys.length === 0) {
    return null
  }

  return (
    <Accordion
      openItems={effectiveOpenItem ? [effectiveOpenItem] : []}
      onToggle={handleToggle}
      collapsible
      className={classes.accordion}>
      {visibleCategoryKeys.map((categoryKey) => {
        const promptsInCategory = groupedPrompts.get(categoryKey) ?? []

        return (
          <div key={categoryKey} className={classes.rowContainer}>
            <AccordionItem value={categoryKey} className={classes.item}>
              <AccordionHeader className={classes.header}>
                {getCategoryLabel(categoryKey, t)}
              </AccordionHeader>
              <AccordionPanel className={classes.panel}>
                <div className={classes.cardsContainer}>
                  {promptsInCategory.map((prompt) => (
                    <PromptCard
                      key={prompt.promptId}
                      id={prompt.promptId}
                      type="standard"
                      title={prompt.title}
                      llmModel={prompt.llmModel}
                      previewText={prompt.description}
                      promptToInsert={prompt.userPrompt}
                      isFavorite={prompt.isFavorite}
                      reviewMode={prompt.reviewMode}
                      requiresDocumentComments={prompt.requiresDocumentComments}
                      systemPrompt={prompt.systemPrompt}
                      requiresFullDocument={prompt.requiresFullDocument}
                    />
                  ))}
                </div>
              </AccordionPanel>
            </AccordionItem>
            <Divider className={classes.divider} />
          </div>
        )
      })}
    </Accordion>
  )
}

export { StandardPromptAccordion }
